import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { BrowserStorage } from 'utils/browserStorage';

export type PostsView = 'calendar' | 'list' | 'board' | 'grid';
export type DateViewMode = 'month' | 'threeMonths' | 'custom';
export type CalendarViewMode = 'week' | 'month';

export const usePostsLayoutState = create<{
  activeView: PostsView;
  leftPanelOpen: boolean;
  postsIdeasActive: boolean;
  inspirationsActive: boolean;
  postsThumbnailsOn: boolean;
  gridMobileViewActive: boolean;
  dateViewMode: DateViewMode;
  calendarViewMode: CalendarViewMode;
  isSidePanelOpen: boolean;
  toggleSidePanel: () => void;
  openPostsPanel: () => void;
  openIdeasPanel: () => void;
  openInspirationsPanel: () => void;
  togglePostsThumbnails: () => void;
  toggleGridMobileView: () => void;
  setActiveView: (view: PostsView) => void;
  setDateViewMode: (mode: DateViewMode) => void;
  setCalendarViewMode: (mode: CalendarViewMode) => void;
  postStatusVariant: 'task' | 'flow';
  togglePostStatusVariant: () => void;
  isPostsPageVisible: boolean;
  togglePostsPageVisibility: () => void;
  isPostsLabelsVisible: boolean;
  togglePostsLabelsVisibility: () => void;
  isBoostBudgetVisible: boolean;
  toggleBoostBudgetVisibility: () => void;
  isInspirationsVisible: boolean;
  toggleInspirationsVisibility: () => void;
  isNewPostExperienceActive: boolean;
  toggleNewPostExperience: () => void;
  setNewPostExperience: (isActive: boolean) => void;
}>()(
  persist(
    (set) => ({
      activeView: 'calendar',
      leftPanelOpen: true,
      postsIdeasActive: false,
      inspirationsActive: false,
      isSidePanelOpen: true,
      postsThumbnailsOn: true,
      gridMobileViewActive: false,
      postStatusVariant: 'flow',
      isPostsPageVisible: true,
      isPostsLabelsVisible: true,
      isBoostBudgetVisible: false,
      isInspirationsVisible: true,
      isNewPostExperienceActive: false,
      dateViewMode: 'month',
      calendarViewMode: 'month',
      setDateViewMode: (mode: DateViewMode) =>
        set(() => ({ dateViewMode: mode })),
      setCalendarViewMode: (mode: CalendarViewMode) =>
        set(() => ({ calendarViewMode: mode })),
      toggleSidePanel: () =>
        set((state) => ({
          isSidePanelOpen: !state.isSidePanelOpen,
          leftPanelOpen:
            !state.isSidePanelOpen &&
            !state.leftPanelOpen &&
            !state.postsIdeasActive &&
            !state.inspirationsActive
              ? true
              : state.leftPanelOpen,
        })),
      openPostsPanel: () =>
        set(() => ({
          isSidePanelOpen: true,
          leftPanelOpen: true,
          postsIdeasActive: false,
          inspirationsActive: false,
        })),
      openIdeasPanel: () =>
        set(() => ({
          isSidePanelOpen: true,
          postsIdeasActive: true,
          leftPanelOpen: false,
          inspirationsActive: false,
        })),
      openInspirationsPanel: () =>
        set(() => ({
          isSidePanelOpen: true,
          inspirationsActive: true,
          postsIdeasActive: false,
          leftPanelOpen: false,
        })),
      togglePostsThumbnails: () =>
        set((state) => ({
          postsThumbnailsOn: !state.postsThumbnailsOn,
        })),
      setActiveView: (activeView: PostsView) =>
        set((state) => {
          const isListOrGrid = ['list', 'grid'].includes(activeView);
          const nextPostsIdeasActive = isListOrGrid
            ? state.leftPanelOpen || state.postsIdeasActive
            : state.postsIdeasActive;
          return {
            leftPanelOpen: isListOrGrid ? false : state.leftPanelOpen,
            postsIdeasActive: nextPostsIdeasActive,
            isSidePanelOpen: isListOrGrid
              ? nextPostsIdeasActive
              : state.isSidePanelOpen,
            activeView,
          };
        }),
      toggleGridMobileView: () =>
        set((state) => ({ gridMobileViewActive: !state.gridMobileViewActive })),
      togglePostStatusVariant: () =>
        set((state) => ({
          postStatusVariant:
            state.postStatusVariant === 'task' ? 'flow' : 'task',
        })),
      togglePostsPageVisibility: () =>
        set((state) => ({ isPostsPageVisible: !state.isPostsPageVisible })),
      togglePostsLabelsVisibility: () =>
        set((state) => ({ isPostsLabelsVisible: !state.isPostsLabelsVisible })),
      toggleBoostBudgetVisibility: () =>
        set((state) => ({ isBoostBudgetVisible: !state.isBoostBudgetVisible })),
      toggleInspirationsVisibility: () =>
        set((state) => ({
          isInspirationsVisible: !state.isInspirationsVisible,
        })),
      toggleNewPostExperience: () =>
        set((state) => ({
          isNewPostExperienceActive: !state.isNewPostExperienceActive,
        })),
      setNewPostExperience: (isActive: boolean) =>
        set(() => ({ isNewPostExperienceActive: isActive })),
    }),
    {
      name: BrowserStorage.keys.Stores.PostsLayout,
      version: 4,
      migrate: (persistedState: unknown, version: number) => {
        const state = persistedState as Record<string, unknown>;

        if (version < 1 && 'isRangeDateViewActive' in state) {
          state.dateViewMode = state.isRangeDateViewActive ? 'custom' : 'month';
          delete state.isRangeDateViewActive;
        }

        if (version < 2) {
          state.isSidePanelOpen =
            Boolean(state.leftPanelOpen) || Boolean(state.postsIdeasActive);
        }

        if (version < 3) {
          if (state.inspirationsActive) {
            state.leftPanelOpen = false;
            state.postsIdeasActive = false;
          } else if (state.postsIdeasActive) {
            state.leftPanelOpen = false;
          }
        }

        if (version < 4 && !state.calendarViewMode) {
          state.calendarViewMode = 'month';
        }

        return state;
      },
    },
  ),
);
